import React, { useState } from 'react';
import { VisualizerState, WORKSHOP_PRESETS } from '../../types/vehicle';
import {
  Camera,
  RotateCcw,
  Sliders,
  Save,
  Check,
  Ruler,
} from 'lucide-react';

export interface StanceToolbarProps {
  state: VisualizerState;
  onLoadPreset: (preset: VisualizerState) => void;
  onUpdateState: (updates: Partial<VisualizerState>) => void;
  onResetDefaults: () => void;
  onExportSnapshot: () => void;
}

export const StanceToolbar: React.FC<StanceToolbarProps> = ({
  state,
  onLoadPreset,
  onUpdateState,
  onResetDefaults,
  onExportSnapshot,
}) => {
  const [savedSuccess, setSavedSuccess] = useState(false);

  const handleSaveToLocalStorage = () => {
    try {
      const savedListRaw = localStorage.getItem('restomod_user_visualizer_presets');
      const list: VisualizerState[] = savedListRaw ? JSON.parse(savedListRaw) : [];
      const updatedList = [
        {
          ...state,
          id: `custom_preset_${Date.now()}`,
          name: `${state.silhouetteId.replace('_', ' ').toUpperCase()} - ${state.paint.presetName || 'CUSTOM'}`,
          updatedAt: new Date().toISOString(),
        },
        ...list.slice(0, 9),
      ];
      localStorage.setItem('restomod_user_visualizer_presets', JSON.stringify(updatedList));
      setSavedSuccess(true);
      setTimeout(() => setSavedSuccess(false), 2500);
    } catch (err) {
      console.warn('Failed to save preset to localStorage:', err);
    }
  };

  const presetKeys = Object.keys(WORKSHOP_PRESETS);

  return (
    <div className="p-3 bg-shop-darkest border border-shop-border rounded flex flex-wrap items-center justify-between gap-3 select-none">
      {/* 1. Workshop Preset Quick Selector */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-mono font-bold uppercase tracking-wider text-shop-muted flex items-center gap-1.5 shrink-0">
          <Sliders className="w-3.5 h-3.5 text-safety-amber" />
          <span>Workshop Presets:</span>
        </span>

        <div className="flex items-center gap-1.5 flex-wrap">
          {presetKeys.map((key) => {
            const p = WORKSHOP_PRESETS[key];
            const isActive = state.id === p.id;
            return (
              <button
                key={key}
                onClick={() => onLoadPreset(p)}
                className={`min-h-[44px] px-3 py-1.5 rounded border text-xs font-mono font-bold uppercase tracking-wider transition-all duration-100 cursor-pointer ${
                  isActive
                    ? 'bg-safety-amber text-shop-darkest border-safety-amberDark shadow-[0_2px_0_0_#92400e]'
                    : 'bg-shop-panel text-shop-light border-shop-border hover:bg-shop-well hover:border-shop-steel'
                }`}
              >
                {p.name.replace('1969 ', '').replace('1932 ', '')}
              </button>
            );
          })}
        </div>
      </div>

      {/* 2. Global Actions: Dimension HUD, Save, Snapshot, Reset */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Toggle Dimension CAD HUD */}
        <button
          onClick={() => onUpdateState({ showDimensionHud: !state.showDimensionHud })}
          className={`min-h-[44px] px-3 py-1.5 rounded border text-xs font-mono font-bold uppercase tracking-wider flex items-center gap-1.5 cursor-pointer transition-all duration-100 ${
            state.showDimensionHud
              ? 'bg-safety-blue text-white border-blue-700 shadow-sm'
              : 'bg-shop-panel text-shop-light border-shop-border hover:bg-shop-well'
          }`}
          title="Toggle Blueprint CAD Measurement HUD Overlay"
        >
          <Ruler className="w-4 h-4 text-blueprint-cyan shrink-0" />
          <span>{state.showDimensionHud ? 'HUD Active' : 'Blueprint HUD'}</span>
        </button>

        {/* Save Current Preset */}
        <button
          onClick={handleSaveToLocalStorage}
          className="min-h-[44px] px-3 py-1.5 rounded bg-shop-panel hover:bg-shop-well text-shop-light border border-shop-border text-xs font-mono font-bold uppercase tracking-wider flex items-center gap-1.5 cursor-pointer active:translate-y-0.5 transition-all duration-100"
          title="Save Current Stance Configuration to Local Storage"
        >
          {savedSuccess ? (
            <>
              <Check className="w-4 h-4 text-safety-green shrink-0" />
              <span className="text-safety-green">Preset Saved</span>
            </>
          ) : (
            <>
              <Save className="w-4 h-4 text-safety-amber shrink-0" />
              <span>Save Preset</span>
            </>
          )}
        </button>

        {/* Download Snapshot PNG */}
        <button
          onClick={onExportSnapshot}
          className="min-h-[44px] px-3.5 py-1.5 rounded bg-safety-amber hover:bg-[#fbbf24] text-shop-darkest border border-safety-amberDark shadow-[0_2px_0_0_#92400e] text-xs font-mono font-bold uppercase tracking-wider flex items-center gap-1.5 cursor-pointer active:translate-y-0.5 transition-all duration-100"
          title="Export and Download Clean PNG Snapshot"
        >
          <Camera className="w-4 h-4 shrink-0" />
          <span>Export PNG</span>
        </button>

        {/* Reset Stance */}
        <button
          onClick={onResetDefaults}
          className="min-h-[44px] min-w-[44px] px-2.5 py-1.5 rounded bg-shop-panel hover:bg-shop-well text-shop-muted hover:text-safety-red border border-shop-border flex items-center justify-center cursor-pointer active:translate-y-0.5 transition-all duration-100"
          title="Reset Visualizer to Default Factory Stance"
        >
          <RotateCcw className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
